<template>
  <div>
    <Nav v-if="!embedded"></Nav>

    <b-container fluid>
      <b-row v-if="!embedded">
        <b-col>
          <h2>Grupos de Modificadores</h2>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col md="6" v-if="!embedded">
          <b-form-group label="Seleccionar Menú:">
            <b-form-select v-model="selectedMenuId" :options="menuOptions" @change="getModifierGroups"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col :md="embedded ? 12 : 6" class="d-flex align-items-end">
          <b-button
            size="sm"
            variant="success"
            @click="createModifierGroup"
            :disabled="!selectedMenuId">
            Crear Grupo de Modificadores
          </b-button>
        </b-col>
      </b-row>
      
      <b-row>
        <b-col>
          <b-form-group label="Buscar:">
            <b-form-input type="search" v-model="search" debounce="500"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
      
      <b-alert :show="!selectedMenuId" variant="info">
        Selecciona un menú para ver sus grupos de modificadores
      </b-alert>
      
      <b-row v-if="selectedMenuId">
        <b-col>
          <b-table 
            small 
            responsive 
            striped 
            hover 
            :items="modifierGroups" 
            :fields="tableFields"
            :filter="search"
            :busy="isLoading">
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Cargando...</strong>
              </div>
            </template>
            <template #cell(is_active)="data">
              <b-badge variant="success" v-if="data.item.is_active">Activo</b-badge>
              <b-badge variant="danger" v-else>Inactivo</b-badge>
            </template>
            <template #cell(item_count)="data">
              <b-badge variant="info">{{ data.item.items?.length || 0 }} artículos</b-badge>
            </template>
            <template #cell(buttons)="data">
              <b-button-group>
                <b-button size="sm" variant="warning" @click="editModifierGroup(data.item)">
                  Editar
                </b-button>
                <b-button size="sm" variant="info" @click="manageItems(data.item)">
                  Gestionar Items
                </b-button>
                <b-button size="sm" variant="danger" @click="confirmDeleteModifierGroup(data.item.id)">
                  Eliminar
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-container>

    <!-- Create/Edit Modifier Group Modal -->
    <b-modal 
      id="modal-modifier-group" 
      :title="isEditing ? 'Editar Grupo de Modificadores' : 'Crear Grupo de Modificadores'" 
      @ok.prevent="submitModifierGroup"
      @hidden="resetModifierGroupForm">
      <form ref="modifierGroupForm" @submit.stop.prevent="submitModifierGroup">
        <b-form-group
          label="Nombre:"
          label-for="name-input"
          :state="nameValidation"
          :invalid-feedback="'El nombre es requerido'">
          <b-form-input
            id="name-input"
            v-model="currentModifierGroup.name"
            :state="nameValidation"
            required
          ></b-form-input>
        </b-form-group>
        
        <b-row>
          <b-col md="6">
            <b-form-group
              label="Mínimo requerido:"
              label-for="min-input"
              description="Cantidad mínima de opciones que el cliente debe seleccionar">
              <b-form-input
                id="min-input"
                v-model.number="currentModifierGroup.required_min"
                type="number"
                min="0"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Máximo permitido:"
              label-for="max-input"
              description="Cantidad máxima de opciones que el cliente puede seleccionar">
              <b-form-input
                id="max-input"
                v-model.number="currentModifierGroup.required_max"
                type="number"
                min="0"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        
        <b-form-group>
          <b-form-checkbox v-model="currentModifierGroup.is_active">
            Grupo Activo
          </b-form-checkbox>
        </b-form-group>
      </form>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../Nav.vue";

export default {
  name: "PlatformModifiers",
  components: { Nav },
  props: {
    embedded: { type: Boolean, default: false },
    menuId: { type: [String, Number], default: "" }
  },
  data() {
    return {
      modifierGroups: [],
      menus: [],
      selectedMenuId: "",
      modifierItems: [],
      availableItems: [],
      isLoading: false,
      isLoadingItems: false,
      isEditing: false,
      search: "",
      currentModifierGroup: {
        id: "",
        menu_id: "",
        name: "",
        required_min: 0,
        required_max: 1,
        is_active: true
      },
      selectedItemToAdd: "",
      itemAdditionPrice: 0,
      itemAdditionMinQty: 0,
      itemAdditionMaxQty: 1,
      itemAdditionSorting: 0,
      tableFields: [
        { key: "name", label: "Nombre", sortable: true },
        { key: "required_min", label: "Min", sortable: true },
        { key: "required_max", label: "Max", sortable: true },
        { key: "item_count", label: "Elementos" },
        { key: "is_active", label: "Estado", sortable: true },
        { key: "buttons", label: "Acciones" }
      ],
      itemFields: [
        { key: "name", label: "Nombre", sortable: true },
        { key: "price", label: "Precio adicional", sortable: true },
        { key: "min_qty", label: "Min", sortable: true },
        { key: "max_qty", label: "Max", sortable: true },
        { key: "is_active", label: "Estado", sortable: true },
        { key: "actions", label: "Acciones" }
      ]
    };
  },
  computed: {
    menuOptions() {
      const options = this.menus.map(menu => ({
        value: menu.id,
        text: menu.name
      }));
      
      return [
        { value: "", text: "Selecciona un menú", disabled: true },
        ...options
      ];
    },
    nameValidation() {
      return this.currentModifierGroup.name ? true : false;
    },
    availableItemOptions() {
      return this.availableItems
        .filter(item => {
          // Filter out items that are already in the modifier group
          const existingItemIds = this.modifierItems.map(i => i.id);
          return !existingItemIds.includes(item.id);
        })
        .map(item => ({
          value: item.id,
          text: `${item.name} (${this.formatPrice(item.price)})`
        }));
    }
  },
  mounted() {
    this.getMenus();

    if (this.embedded) {
      if (this.menuId) {
        this.selectedMenuId = this.menuId;
        this.getModifierGroups();
      }
    } else if (this.$route.params.menu_id) {
      // Check if menu_id is in route params
      this.selectedMenuId = this.$route.params.menu_id;
      this.getModifierGroups();
    }
  },
  watch: {
    menuId(val) {
      if (!this.embedded) return;
      this.selectedMenuId = val || "";
      if (this.selectedMenuId) {
        this.getModifierGroups();
      } else {
        this.modifierGroups = [];
      }
    }
  },
  methods: {
    getMenus() {
      axios
        .get("/platform/menus")
        .then(response => {
          this.menus = response.data;
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener menús", "danger");
        });
    },
    getModifierGroups() {
      if (!this.selectedMenuId) return;
      
      this.isLoading = true;
      axios
        .get(`/platform/menu/${this.selectedMenuId}/modifiergroups`)
        .then(response => {
          this.modifierGroups = response.data;
          
          // For each modifier group, get the items count (this could be optimized with backend support)
          for (const group of this.modifierGroups) {
            this.getModifierGroupItems(group.id, true);
          }
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener grupos de modificadores", "danger");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getAvailableItems() {
      if (!this.selectedMenuId) return;
      
      axios
        .get(`/platform/menu/${this.selectedMenuId}/items`)
        .then(response => {
          // Filter to only show items with type MODIFIER
          this.availableItems = response.data.filter(item => item.type === "MODIFIER" || item.type === "PRODUCT");
        })
        .catch(err => {
          this.makeToast("Error", err.response?.data?.message || "Error al obtener artículos disponibles", "danger");
        });
    },
    getModifierGroupItems(groupId, countsOnly = false) {
      axios
        .get(`/platform/modifiergroup/${groupId}/items`)
        .then(response => {
          if (countsOnly) {
            // Just update the count in the modifier groups list
            const index = this.modifierGroups.findIndex(group => group.id === groupId);
            if (index !== -1) {
              this.$set(this.modifierGroups[index], 'items', response.data);
            }
          } else {
            // Update the full list of items for display
            this.modifierItems = response.data;
          }
        })
        .catch(err => {
          console.error(`Error fetching items for modifier group ${groupId}:`, err);
        });
    },
    createModifierGroup() {
      this.isEditing = false;
      this.currentModifierGroup = {
        id: "",
        menu_id: this.selectedMenuId,
        name: "",
        required_min: 0,
        required_max: 1,
        is_active: true
      };
      this.$bvModal.show("modal-modifier-group");
    },
    editModifierGroup(modifierGroup) {
      this.isEditing = true;
      this.currentModifierGroup = { ...modifierGroup };
      this.$bvModal.show("modal-modifier-group");
    },
    submitModifierGroup() {
      if (!this.currentModifierGroup.name) {
        return; // Validation failed
      }

      const method = this.isEditing ? "PUT" : "POST";
      const url = this.isEditing 
        ? `/platform/modifiergroup/${this.currentModifierGroup.id}` 
        : `/platform/menu/${this.selectedMenuId}/modifiergroup`;
      
      axios({
        method,
        url,
        data: this.currentModifierGroup
      })
        .then(() => {
          this.makeToast(
            "Éxito", 
            `Grupo de modificadores ${this.isEditing ? 'actualizado' : 'creado'} exitosamente`, 
            "success"
          );
          this.getModifierGroups();
          this.$bvModal.hide("modal-modifier-group");
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || `Error al ${this.isEditing ? 'actualizar' : 'crear'} el grupo de modificadores`, 
            "danger"
          );
        });
    },
    confirmDeleteModifierGroup(id) {
      this.$bvModal.msgBoxConfirm("¿Estás seguro que deseas eliminar este grupo de modificadores? Esta acción no se puede deshacer.", {
        title: "Confirmar eliminación",
        okVariant: "danger",
        okTitle: "Eliminar",
        cancelTitle: "Cancelar",
        centered: true
      })
      .then(result => {
        if (result) {
          this.deleteModifierGroup(id);
        }
      });
    },
    deleteModifierGroup(id) {
      axios
        .delete(`/platform/modifiergroup/${id}`)
        .then(() => {
          this.makeToast("Éxito", "Grupo de modificadores eliminado exitosamente", "success");
          this.getModifierGroups();
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al eliminar el grupo de modificadores", 
            "danger"
          );
        });
    },
    manageItems(modifierGroup) {
      this.currentModifierGroup = { ...modifierGroup };
      this.isLoadingItems = true;
      
      // Get items in this modifier group
      this.getModifierGroupItems(modifierGroup.id);
      
      // Get all available items for adding
      this.getAvailableItems();
      
      this.$bvModal.show("modal-manage-items");
      this.isLoadingItems = false;
    },
    addItemToGroup() {
      if (!this.selectedItemToAdd) return;
      
      const selectedItem = this.availableItems.find(item => item.id === this.selectedItemToAdd);
      if (!selectedItem) return;
      
      const data = {
        min_qty: this.itemAdditionMinQty,
        max_qty: this.itemAdditionMaxQty,
        price: this.itemAdditionPrice,
        sorting_position: this.itemAdditionSorting
      };
      
      axios
        .post(`/platform/modifiergroup/${this.currentModifierGroup.id}/item/${this.selectedItemToAdd}`, data)
        .then(() => {
          this.makeToast("Éxito", "Opción agregada exitosamente", "success");
          this.getModifierGroupItems(this.currentModifierGroup.id);
          this.resetItemAdditionForm();
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al agregar la opción", 
            "danger"
          );
        });
    },
    removeItemFromGroup(itemId) {
      axios
        .delete(`/platform/modifiergroup/${this.currentModifierGroup.id}/item/${itemId}`)
        .then(() => {
          this.makeToast("Éxito", "Opción eliminada exitosamente", "success");
          this.getModifierGroupItems(this.currentModifierGroup.id);
        })
        .catch(err => {
          this.makeToast(
            "Error", 
            err.response?.data?.message || "Error al eliminar la opción", 
            "danger"
          );
        });
    },
    resetModifierGroupForm() {
      this.currentModifierGroup = {
        id: "",
        menu_id: this.selectedMenuId,
        name: "",
        required_min: 0,
        required_max: 1,
        is_active: true
      };
      this.isEditing = false;
    },
    resetItemsModal() {
      this.modifierItems = [];
      this.resetItemAdditionForm();
    },
    resetItemAdditionForm() {
      this.selectedItemToAdd = "";
      this.itemAdditionPrice = 0;
      this.itemAdditionMinQty = 0;
      this.itemAdditionMaxQty = 1;
      this.itemAdditionSorting = 0;
    },
    formatPrice(price) {
      return Number(price).toFixed(2);
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>
